import { useState } from 'react'
import classNames from 'classnames'
import { Checkbox } from '@winnin/winnin_ui.components.checkbox'

import BlockComponentsRole75Checker from 'components/BlockComponentsRole75Checker'
import type { TooltipDirection } from 'components/RCTooltip'
import { RCTooltip } from 'components/RCTooltip'
import { useSelectedItemsList } from 'contexts/SelectedItemsListProvider'
import { useTranslate } from 'lib/tolgee'
import { TYPE_TO_KEY } from 'modules/territory/common/territory.consts'
import { UserStore } from 'stores'
import { Filters } from 'utils'
import './index.scss'

const TOLGEE_PREFIX = 'TerritoryAddToCheckbox'

interface IEntityObject {
  id: string
  name: string
  type: string
}

interface ITerritoryAddToCheckbox {
  id: string
  tooltipStyle?: TooltipDirection
  entityType: string
  entityObject?: IEntityObject
  checkboxSize?: number | string
}

function TerritoryAddToCheckbox({
  id,
  tooltipStyle = 'bottom',
  entityType,
  entityObject,
  checkboxSize,
}: Readonly<ITerritoryAddToCheckbox>) {
  const [isLoading, setIsLoading] = useState(false)
  const t = useTranslate()
  const { selectedItemsBar, removeItem, addItem } = useSelectedItemsList()

  const isCheckboxActive = selectedItemsBar?.some(item => item?.id === id)
  const isUserViewer = UserStore.getRole === 'viewer'
  const isUserAllowed = UserStore?.userInfo?.role > 50

  const checkboxTooltip = t({
    key: `${TOLGEE_PREFIX}-tooltip-ownedTopic-Tag`,
    parameters: { entityType },
    defaultValue: 'Select {entityType, select, saved_search {this owned topic} tag {this tag} other {this topic}}',
  })

  function handleClickSelection(event) {
    event?.preventDefault()
    event?.stopPropagation()

    if (isLoading) {
      return
    }

    const territoryId = `${entityType}.${id}`

    const isItemSelected = selectedItemsBar.find(item => item?.id === id)

    if (isItemSelected) {
      return removeItem(isItemSelected)
    }

    if (entityObject) {
      addItem(entityObject)
      return
    }

    setIsLoading(true)
    Filters.getTerritoryById(territoryId)
      .then(data => {
        const territoryData = data ?? {}

        const addToValue = {
          id: territoryData?.value,
          name: territoryData?.label,
          type: TYPE_TO_KEY?.[entityType],
          ...(territoryData?.parent && { parent: territoryData.parent }),
          ...(territoryData?.language && { language: territoryData.language }),
          ...(territoryData?.topic && { topic: territoryData.topic }),
          ...(territoryData?.query && { query: territoryData.query }),
          ...(territoryData?.category && { category: territoryData.category }),
        }

        addItem(addToValue)
      })
      .catch(error => {
        console.error('TerritoryAddToCheckbox @ handleClickSelection >>>>>', error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  if (isUserViewer) {
    return null
  }

  return (
    <div className={classNames(['TerritoryAddToCheckbox', { 'TerritoryAddToCheckbox-active': isCheckboxActive }])}>
      <BlockComponentsRole75Checker isClickBlocked={!isUserAllowed}>
        <RCTooltip tooltipMessage={checkboxTooltip} direction={tooltipStyle}>
          <Checkbox
            size={checkboxSize}
            className="TerritoryAddToCheckbox-checkbox"
            isActive={isLoading || isCheckboxActive}
            handleClick={handleClickSelection}
          />
        </RCTooltip>
      </BlockComponentsRole75Checker>
    </div>
  )
}

export default TerritoryAddToCheckbox
